import * as core from "@envstash/core-wasm";
import type { Account, AuthResult, KdfParams, WrappedVaultKey } from "@envstash/protocol";
import { api, isDefinedError, safe } from "./api.js";
import { derivePasswordKeysAsync } from "./argon.js";
import { b64Encode } from "./bytes.js";
import type { DeviceRecord } from "./db.js";
import { getOrCreateDeviceKey, rememberDeviceId, unwrapWithThisDevice, wrapForThisDevice } from "./device-key.js";
import { Store } from "./store.js";
import { vaultStore } from "./vault-store.js";

export type SessionState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "locked"; account: Account; vaultId: string; deviceId: string | null; wraps: WrappedVaultKey[] }
  | {
      status: "unlocked";
      account: Account;
      vaultId: string;
      deviceId: string;
      wraps: WrappedVaultKey[];
      vk: Uint8Array;
    };

export const session = new Store<SessionState>({ status: "loading" });

let deviceRecord: DeviceRecord | null = null;
export async function deviceKey(): Promise<DeviceRecord> {
  deviceRecord ??= await getOrCreateDeviceKey();
  return deviceRecord;
}

export async function newDeviceInput(
  name = defaultBrowserName(),
): Promise<{ public_key: string; name: string; platform: "browser" }> {
  const rec = await deviceKey();
  return { public_key: b64Encode(rec.publicKey), name, platform: "browser" };
}

export function defaultBrowserName(): string {
  const ua = navigator.userAgent;
  const browser = ua.includes("Edg/")
    ? "Edge"
    : ua.includes("Chrome/")
      ? "Chrome"
      : ua.includes("Safari/")
        ? "Safari"
        : ua.includes("Firefox/")
          ? "Firefox"
          : "Browser";
  const os = ua.includes("Mac")
    ? "macOS"
    : ua.includes("Windows")
      ? "Windows"
      : ua.includes("Linux")
        ? "Linux"
        : ua.includes("iPhone") || ua.includes("iPad")
          ? "iOS"
          : "";
  return os ? `${browser} on ${os}` : browser;
}

/** Make sure the server holds a device wrap for this browser, so reloads unlock silently. */
async function ensureDeviceWrap(result: AuthResult, vk: Uint8Array): Promise<WrappedVaultKey[]> {
  if (!result.device_id) return result.wraps;
  const has = result.wraps.some((w) => w.kind === "device" && w.device_id === result.device_id);
  if (has) return result.wraps;
  const rec = await deviceKey();
  const wrap = wrapForThisDevice(vk, rec, result.device_id);
  await api.devices.setWrap({ device_wrap: wrap });
  return [...result.wraps, wrap];
}

async function becomeUnlocked(result: AuthResult, vk: Uint8Array): Promise<void> {
  if (!result.device_id) {
    session.set({
      status: "locked",
      account: result.account,
      vaultId: result.vault_id,
      deviceId: null,
      wraps: result.wraps,
    });
    return;
  }
  await rememberDeviceId(result.device_id);
  const wraps = await ensureDeviceWrap(result, vk);
  session.set({
    status: "unlocked",
    account: result.account,
    vaultId: result.vault_id,
    deviceId: result.device_id,
    wraps,
    vk,
  });
  vaultStore.start(vk, result.vault_id, result.device_id);
}

function becomeLocked(result: AuthResult): void {
  session.set({
    status: "locked",
    account: result.account,
    vaultId: result.vault_id,
    deviceId: result.device_id,
    wraps: result.wraps,
  });
}

/** Try to open the vault with this browser's device key. */
async function trySilentUnlock(result: AuthResult): Promise<Uint8Array | null> {
  if (!result.device_id) return null;
  const wrap = result.wraps.find((w) => w.kind === "device" && w.device_id === result.device_id);
  if (!wrap) return null;
  try {
    return await unwrapWithThisDevice(wrap, await deviceKey());
  } catch {
    return null;
  }
}

/** Called once at startup. */
export async function bootstrapSession(): Promise<void> {
  await core.initCore();
  const [err, me] = await safe(api.auth.me());
  if (err) {
    session.set({ status: "anon" });
    return;
  }
  const vk = await trySilentUnlock(me);
  if (vk) await becomeUnlocked(me, vk);
  else becomeLocked(me);
}

export async function loginWithPassword(email: string, password: string): Promise<void> {
  const { kdf } = await api.auth.loginStart({ email });
  const { wrapKey, authKey } = await derivePasswordKeysAsync(password, kdf);
  const result = await api.auth.login({ email, auth_key: b64Encode(authKey), device: await newDeviceInput() });
  const pw = result.wraps.find((w) => w.kind === "password");
  if (!pw) throw new Error("No password wrap on this account");
  const vk = core.unwrapPassword(pw, wrapKey);
  await becomeUnlocked(result, vk);
}

/** Unlock an existing session with the password (e.g. after a passkey login without PRF). */
export async function unlockWithPassword(password: string): Promise<void> {
  const s = session.get();
  if (s.status !== "locked") return;
  let pw = s.wraps.find((w) => w.kind === "password");
  if (!pw) {
    const me = await api.auth.me();
    pw = me.wraps.find((w) => w.kind === "password");
  }
  if (!pw) throw new Error("No password wrap on this account");
  const { wrapKey } = await derivePasswordKeysAsync(password, pw.kdf);
  const vk = core.unwrapPassword(pw, wrapKey);
  let result: AuthResult = { account: s.account, vault_id: s.vaultId, device_id: s.deviceId, wraps: s.wraps };
  if (!result.device_id) {
    // Session without a device (older login): re-login is needed to attach one.
    result = await api.auth.me();
  }
  await becomeUnlocked(result, vk);
}

export interface SignupInput {
  invite: string;
  email: string;
  name: string;
  password: string;
}

/** Full client-side signup. Returns the recovery key to show exactly once. */
export async function signup(input: SignupInput, onProgress?: (step: string) => void): Promise<string> {
  onProgress?.("Generating keys");
  const vk = core.generateVaultKey();
  const kdf: KdfParams = core.newKdfParams();
  const recoveryKey = core.generateRecoveryKey();
  onProgress?.("Deriving password key (this takes a second)");
  const { wrapKey, authKey } = await derivePasswordKeysAsync(input.password, kdf);
  const rec = await deviceKey();
  onProgress?.("Creating account");
  const result = await api.auth.signup({
    invite_token: input.invite,
    email: input.email,
    name: input.name,
    auth_key: b64Encode(authKey),
    recovery_auth_key: b64Encode(core.recoveryAuthKey(recoveryKey)),
    password_wrap: core.wrapPassword(vk, wrapKey, kdf),
    recovery_wrap: core.wrapRecovery(vk, recoveryKey),
    device: await newDeviceInput(),
    device_wrap: wrapForThisDevice(vk, rec, null),
  });
  await becomeUnlocked(result, vk);
  return recoveryKey;
}

export async function recoverAccount(
  email: string,
  recoveryKeyInput: string,
  newPassword: string,
  onProgress?: (s: string) => void,
): Promise<void> {
  const recoveryKey = core.normalizeRecoveryKey(recoveryKeyInput);
  const recoveryAuth = b64Encode(core.recoveryAuthKey(recoveryKey));
  onProgress?.("Checking recovery key");
  const { recovery_wrap } = await api.auth.recoveryFetch({ email, recovery_auth_key: recoveryAuth });
  const vk = core.unwrapRecovery(recovery_wrap, recoveryKey);
  onProgress?.("Deriving new password key");
  const kdf = core.newKdfParams();
  const { wrapKey, authKey } = await derivePasswordKeysAsync(newPassword, kdf);
  const rec = await deviceKey();
  onProgress?.("Resetting password");
  const result = await api.auth.recoveryReset({
    email,
    recovery_auth_key: recoveryAuth,
    new_auth_key: b64Encode(authKey),
    new_password_wrap: core.wrapPassword(vk, wrapKey, kdf),
    device: await newDeviceInput(),
    device_wrap: wrapForThisDevice(vk, rec, null),
  });
  await becomeUnlocked(result, vk);
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const s = session.get();
  if (s.status !== "unlocked") throw new Error("Vault is locked");
  const pw = s.wraps.find((w) => w.kind === "password");
  if (!pw) throw new Error("No password wrap");
  const cur = await derivePasswordKeysAsync(currentPassword, pw.kdf);
  // Verify locally before asking the server, so a typo gives a clear error.
  core.unwrapPassword(pw, cur.wrapKey);
  const kdf = core.newKdfParams();
  const next = await derivePasswordKeysAsync(newPassword, kdf);
  const newWrap = core.wrapPassword(s.vk, next.wrapKey, kdf);
  await api.auth.changePassword({
    current_auth_key: b64Encode(cur.authKey),
    new_auth_key: b64Encode(next.authKey),
    new_password_wrap: newWrap,
  });
  session.set({ ...s, wraps: s.wraps.map((w) => (w.kind === "password" ? newWrap : w)) });
}

export async function rotateRecoveryKey(): Promise<string> {
  const s = session.get();
  if (s.status !== "unlocked") throw new Error("Vault is locked");
  const recoveryKey = core.generateRecoveryKey();
  await api.auth.rotateRecoveryKey({
    recovery_auth_key: b64Encode(core.recoveryAuthKey(recoveryKey)),
    recovery_wrap: core.wrapRecovery(s.vk, recoveryKey),
  });
  return recoveryKey;
}

/** Adopt an auth result from a passkey login, unlocking with whatever we can. */
export async function adoptAuthResult(
  result: AuthResult,
  prfOutput: Uint8Array | null,
  credentialId: string | null,
): Promise<void> {
  if (prfOutput && credentialId) {
    const w = result.wraps.find((x) => x.kind === "passkey" && x.credential_id === credentialId);
    if (w) {
      try {
        await becomeUnlocked(result, core.unwrapPasskey(w, prfOutput));
        return;
      } catch {
        // fall through to the device wrap
      }
    }
  }
  const vk = await trySilentUnlock(result);
  if (vk) await becomeUnlocked(result, vk);
  else becomeLocked(result);
}

export async function logout(): Promise<void> {
  vaultStore.stop();
  const [err] = await safe(api.auth.logout());
  if (err && !isDefinedError(err)) throw err;
  session.set({ status: "anon" });
}

export function lockVault(): void {
  const s = session.get();
  if (s.status !== "unlocked") return;
  vaultStore.stop();
  session.set({ status: "locked", account: s.account, vaultId: s.vaultId, deviceId: s.deviceId, wraps: s.wraps });
}

/** Called when the server tells us this device was revoked or the session died. */
export function sessionLost(): void {
  vaultStore.stop();
  session.set({ status: "anon" });
}
